import { useTranslation } from "react-i18next";
import type { ConsentTopic, TopicKey } from "../../api/consent";

/** Opens one consent type's page on the legal site in a new tab (never several consents in the app at once). */
export function openConsentPage(topic: Pick<ConsentTopic, "url">): void {
  if (topic.url) window.open(topic.url, "_blank", "noopener,noreferrer");
}

/**
 * The shared checkbox list of every consent screen (Figma "Shared / Privacy / Consent checkbox row" 4420:1902):
 * a box, the title, the one-line summary and "More" (opens the type's page). Boxes come unticked from the caller.
 * With `required` keys, the rows are grouped under Required / Optional.
 */
export default function ConsentChecklist({
  topics,
  required = [],
  ticked,
  onToggle,
  onRead = openConsentPage,
  disabled = false,
  testIdPrefix = "consent",
}: {
  topics: ConsentTopic[];
  required?: TopicKey[];
  ticked: ReadonlySet<TopicKey>;
  onToggle: (key: TopicKey) => void;
  onRead?: (topic: ConsentTopic) => void;
  disabled?: boolean;
  /** Test ids are `<prefix>-item-<key>` / `<prefix>-more-<key>` (Settings → Privacy uses "privacy"). */
  testIdPrefix?: string;
}) {
  const { t } = useTranslation();
  const req = topics.filter((x) => required.includes(x.key));
  const opt = topics.filter((x) => !required.includes(x.key));

  const rows = (list: ConsentTopic[]) =>
    list.map((topic) => (
      <ConsentRow
        key={topic.key}
        topic={topic}
        checked={ticked.has(topic.key)}
        onToggle={() => onToggle(topic.key)}
        onRead={() => onRead(topic)}
        disabled={disabled}
        moreLabel={t("consent_more")}
        testIdPrefix={testIdPrefix}
      />
    ));

  if (req.length === 0) {
    return <div className="flex flex-col gap-3">{rows(opt)}</div>;
  }
  return (
    <div className="flex flex-col gap-3">
      <GroupTitle>{t("consent_group_required")}</GroupTitle>
      {rows(req)}
      {opt.length > 0 && (
        <>
          <GroupTitle>{t("consent_group_optional")}</GroupTitle>
          {rows(opt)}
        </>
      )}
    </div>
  );
}

function GroupTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs font-semibold uppercase tracking-wide text-[var(--foreground-muted)]">
      {children}
    </div>
  );
}

function ConsentRow({
  topic,
  checked,
  onToggle,
  onRead,
  disabled,
  moreLabel,
  testIdPrefix,
}: {
  topic: ConsentTopic;
  checked: boolean;
  onToggle: () => void;
  onRead: () => void;
  disabled: boolean;
  moreLabel: string;
  testIdPrefix: string;
}) {
  const id = `${testIdPrefix}-${topic.key}`;
  return (
    <div className="flex items-start gap-3" data-consent-row={topic.key}>
      <input
        id={id}
        type="checkbox"
        data-testid={`${testIdPrefix}-item-${topic.key}`}
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        className="mt-0.5 h-[18px] w-[18px] shrink-0 cursor-pointer accent-[var(--primary-fill)]"
      />
      <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
        <span className="block text-sm text-[var(--foreground)]">{topic.title}</span>
        {topic.summary && (
          <span className="block text-xs text-[var(--foreground-muted)]">{topic.summary}</span>
        )}
      </label>
      {topic.url && (
        <button
          type="button"
          data-testid={`${testIdPrefix}-more-${topic.key}`}
          onClick={onRead}
          className="shrink-0 self-center text-sm font-semibold text-[var(--primary-text)] hover:underline"
        >
          {moreLabel}
        </button>
      )}
    </div>
  );
}
